/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { type ShopContextValue, ShopProvider as CommerceShopProvider } from "@farmlands/commerce";
import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  farmlandsProApi,
  isFarmlandsProApiAccountNumberApplied,
} from "../../../services/farmlandspro-api";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { URLS } from "../../../util/consts";
import { useBranch } from "../../../util/useBranch";
import {
  selectCurrentAccountId,
  selectCurrentFarmerAccount,
  selectEmailForPrefill,
  selectIsThisTfo,
  selectNameForPrefill,
  selectPhoneForPrefill,
} from "../../azure-b2c/storage";
import { selectCurrentCart } from "../../cart/storage/cart.slice";

export interface ShopProviderProps {
  children: ReactNode;
  productListUrl: string;
  cartPageUrl: string;
}

export const ShopProvider = ({ children, productListUrl, cartPageUrl }: ShopProviderProps) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const userId = useSelector(selectCurrentAccountId);
  const branchId = useBranch();
  const isTfo = useSelector(selectIsThisTfo);
  const remoteConfig = useSelector(selectRemoteConfig);
  const cart = useSelector(selectCurrentCart);
  const contactName = useSelector(selectNameForPrefill);
  const contactEmail = useSelector(selectEmailForPrefill);
  const contactPhone = useSelector(selectPhoneForPrefill);

  const value: ShopContextValue = {
    apiClient: farmlandsProApi,
    identity: {
      userId: userId ?? null,
      accountName: currentAccount?.accountName ?? null,
      selectedAccountNumber: currentAccount?.accountNumber ?? null,
      branchId: branchId ?? null,
      isReady: isFarmlandsProApiAccountNumberApplied(currentAccount?.accountNumber),
      isTfo: isTfo ?? false,
      contactName: contactName?.trim() || null,
      contactEmail: contactEmail ?? null,
      contactPhone: contactPhone ?? null,
    },
    config: {
      productListUrl,
      cartPageUrl,
      typesense: {
        host: process.env.REACT_APP_TYPESENSE_HOST ?? "",
        apiKey: process.env.REACT_APP_TYPESENSE_API_PUBLIC_KEY ?? "",
      },
      featureFlags: {
        traderDealsEnabled: remoteConfig.traderDealsEnabled,
        traderDealsFilterValue: remoteConfig.traderDealsFilterValue,
        buyBreakEnabled: remoteConfig.buyBreaksEnabled,
      },
      cart: {
        freeDeliveryThreshold: remoteConfig.freeDeliveryThreshold,
        termsAndConditionsUrl: URLS.salesOrdersTermsAndConditions,
        fulfilmentFaqsUrl: URLS.fulfilmentFAQs,
        contactUsUrl: remoteConfig.contactUrl,
      },
    },
    cart,
    triggerToast: toast,
  };

  return <CommerceShopProvider value={value}>{children}</CommerceShopProvider>;
};
